/*
<summary>
   This functional component is all about rendering the Loan Out Tool popup and
   submitting the loan-out details (lender/sub-contractor, scheduled return date) to API.
   Developer: Aashish Singh, Created Date: 20-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useStore } from '../../../contexts/store-provider';
import { BorrowerType } from '../../../constants/constants';
import LoanRecordEnum from '../../../constants/enums/loan-record-enum';
import LoanStatusEnum from '../../../constants/enums/loan-status-enum';
import { getUserId, getTenantIdFromLocalStorage } from '../../../helpers/localstorage-helper';
import { IOption } from '../../../models/ICommon';
import { ILoanOutToolRequest } from '../../../models/forms/IAddUpdateToolManagement';
import { formatMessage } from '../../../translations/format-message';
import LoanOutToolForm, {
  ILoanOutToolFormValues,
  SUB_CONTRACTOR_LENDER_VALUE,
} from '../forms/loan-out-tool-form';

interface IProps {
  toolId: number;
  toolManagementNo: string;
  lenderFactoryId: number;
  lenderFactoryName: string;
  factoryOptions: IOption[];
  modalClosed: () => void;
  onSuccess: () => void;
}

/**
* This function is used to format a Date as "yyyy-MM-ddT00:00:00" — the loan date/planned
* return date fields are date-level only, so the time portion is always midnight.
* @param date: Date to format
*/
const formatDateTimeMidnight = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T00:00:00`;
};

/**
* This functional component renders the Loan Out Tool form and submits the loan-out
* request for the selected tool.
* @param props: Tool identifier, factory options and modal close handler
*/
const LoanOutTool = (props: IProps) => {
  const { toolManagementStore, factoryStore } = useStore();
  const { LoanOutToolService, resetLoanOutToolState, loanOutToolState } =
    toolManagementStore;
  const { allFactorys } = factoryStore;
  const lenderFactoryId = props.lenderFactoryId;
  const lenderFactoryName = props.lenderFactoryName;

  /**
  * This function is used to look up a factory's DB-level FactoryId 
  * @param tenantId: Factory's numeric TenantId
  */
  const getDbFactoryName = (tenantId: number | null): string =>
    (tenantId != null && allFactorys.find((f) => Number(f.TenantId) === tenantId)?.FactoryId) || '';

  useEffect(() => {
    if (loanOutToolState.success) {
      toast.success(formatMessage('tool_loaned_out_success'));
      resetLoanOutToolState();
      props.modalClosed();
      props.onSuccess();
    }
  }, [loanOutToolState.success]);

  useEffect(() => {
    if (loanOutToolState.error) {
      toast.error(formatMessage(loanOutToolState.error));
      resetLoanOutToolState();
    }
  }, [loanOutToolState.error]);

  /**
  * This function is used to build the AddToolLoanDetails request body from the form values and acll the API function.
  * @param values: Loan Out Tool form values
  */
  const onSubmitForm = (values: ILoanOutToolFormValues) => {
    const isSubContractor = values.Lender === SUB_CONTRACTOR_LENDER_VALUE;
    const plannedReturnDate =
      values.ScheduledReturnDate instanceof Date
        ? formatDateTimeMidnight(values.ScheduledReturnDate)
        : '';
    const borrowerFactoryId = isSubContractor ? null : Number(values.Lender);

    const request: ILoanOutToolRequest = {
      ToolId: props.toolId,
      ToolNumber: props.toolManagementNo,
      LenderFactoryId: lenderFactoryId,
      LenderFactoryName: lenderFactoryName,
      DbLenderFactoryName: getDbFactoryName(lenderFactoryId),
      BorrowerFactoryId: borrowerFactoryId,
      BorrowerFactoryName: isSubContractor
        ? null
        : props.factoryOptions.find((f) => f.id === borrowerFactoryId)?.value || '',
      DbBorrowerFactoryName: isSubContractor ? null : getDbFactoryName(borrowerFactoryId),
      BorrowerType: isSubContractor ? BorrowerType.Subcontractor : BorrowerType.Factory,
      SubcontractorName: isSubContractor ? values.SubContractor.trim() : '',
      LoanDate: formatDateTimeMidnight(new Date()),
      PlannedReturnDate: plannedReturnDate,
      ActualReturnDate: null,
      LoanStatus: LoanStatusEnum.Active,
      LoanedBy: Number(getUserId()) || 0,
      ReturnedBy: null,
      Remarks: null,
      LoanRecord: LoanRecordEnum.Yes,
      TenantId: Number(getTenantIdFromLocalStorage()),
    };

    LoanOutToolService(request);
  };

  return (
    <LoanOutToolForm
      factoryOptions={props.factoryOptions}
      lenderFactoryId={lenderFactoryId}
      lenderFactoryName={lenderFactoryName}
      isLoading={loanOutToolState.inProgress}
      onModalClosed={props.modalClosed}
      submitHandler={onSubmitForm}
    />
  );
};

export default observer(LoanOutTool);
